import { useState, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { GLOSSARY, type GlossaryKey } from "./glossary";

interface TermProps {
  term: GlossaryKey;
  children: ReactNode;
  /** Extra row-specific text shown under the definition. */
  detail?: string;
}

/**
 * Wraps a label so hovering, focusing or tapping it explains the term.
 * Unknown terms render as plain text.
 */
const Term = ({ term, children, detail }: TermProps) => {
  const [open, setOpen] = useState(false);
  const definition = GLOSSARY[term];
  if (!definition) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            onClick={() => setOpen(value => !value)}
            className="cursor-help underline decoration-dotted underline-offset-4"
          >
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {definition}
          {detail ? <div className="mt-1 text-muted-foreground">{detail}</div> : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default Term;
